import { Material } from '../types/material.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit, Trash2, History, Package, Wrench } from 'lucide-react';
import { formatoPrecio } from '@/lib/utils/formato-precio';

interface MaterialTableProps {
  materials: Material[];
  onEdit: (material: Material) => void;
  onDelete: (material: Material) => void;
  onViewTariffHistory: (material: Material) => void;
  isLoading?: boolean;
}

export function MaterialTable({
  materials,
  onEdit,
  onDelete,
  onViewTariffHistory,
  isLoading,
}: MaterialTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Cargando materiales...</p>
      </div>
    );
  }

  if (!materials || materials.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground font-medium">No hay materiales configurados</p>
        <p className="text-xs text-muted-foreground mt-1">
          Registra materiales con su partida arancelaria y gravamen para usarlos como items de línea
          en cotizaciones
        </p>
      </div>
    );
  }

  /*
    El costo nacionalizado que pasó Comercia («Usar estos costos en Cotiza»). Sólo
    aparece si algún material lo tiene: «Ver en Materiales» llevaba a una tabla que
    no lo mostraba.
  */
  const conCosto = materials.some((m) => m.lastLandedCost != null);

  return (
    // Con desplazamiento propio: a 390 px la tabla empujaba la página entera.
    <div className="border rounded-lg overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Cod. Externo</TableHead>
            <TableHead>SAP</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Línea de Negocio</TableHead>
            <TableHead>Partida Arancelaria</TableHead>
            <TableHead className="text-right">Gravamen %</TableHead>
            {conCosto && <TableHead className="text-right">Costo nacionalizado</TableHead>}
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {materials.map((material) => (
            <TableRow key={material.id}>
              <TableCell className="font-mono font-semibold">{material.code}</TableCell>
              <TableCell>
                {material.type === 'SERVICE' ? (
                  <Badge variant="secondary" className="gap-1">
                    <Wrench className="w-3 h-3" /> Servicio
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1">
                    <Package className="w-3 h-3" /> Producto
                  </Badge>
                )}
              </TableCell>
              <TableCell className="font-mono text-muted-foreground">
                {material.externalCode || '-'}
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                {(material as any).sapCode || '—'}
              </TableCell>
              <TableCell>{material.name}</TableCell>
              <TableCell>
                {material.lob ? (
                  <Badge variant="secondary">{material.lob.name}</Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="font-mono">{material.tariffHeading || '-'}</TableCell>
              <TableCell className="text-right">
                {material.activeTariffRate != null ? (
                  <Badge variant="outline">{material.activeTariffRate}%</Badge>
                ) : (
                  '-'
                )}
              </TableCell>
              {conCosto && (
                <TableCell className="text-right">
                  {material.lastLandedCost != null ? (
                    <>
                      <p className="font-semibold tabular-nums">
                        USD {formatoPrecio(material.lastLandedCost)}
                      </p>
                      {material.lastImportDate && (
                        <p className="text-xs text-muted-foreground">
                          por unidad · Comercia,{' '}
                          {new Date(material.lastImportDate).toLocaleDateString('es-BO')}
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              )}
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onViewTariffHistory(material)}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Ver historial de cambios en el gravamen arancelario
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(material)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar datos del material</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(material)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Eliminar este material</TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default MaterialTable;
